import { TabListBaseProps, filterRankSortTokens } from "./shared";
import { TokenList } from "../token-list";
import {
  TokenInfoWithPrice,
  useFavoriteTokensQuery,
  useTokensQuery,
} from "@/api";
import { useUserSolType } from "@/hooks";
import { useDeferredValue } from "react";
import { EmptyState, Icon } from "@/components";

const EMPTY_LIST: TokenInfoWithPrice[] = [];

interface FavoriteListProps extends TabListBaseProps {
  search: string;
  extraHeight?: number;
}

export function FavoriteList({
  search,
  onSelectToken,
  scrollRef,
  extraHeight,
}: FavoriteListProps) {
  const solType = useUserSolType();
  const favoriteTokenQuery = useFavoriteTokensQuery();
  const favorites = favoriteTokenQuery.data?.map((x) => x.address) ?? [];
  const tokenInfoMap = useTokensQuery({
    solType,
    list_address: favorites,
  }).data?.data.reduce<Record<string, TokenInfoWithPrice>>((acc, token) => {
    acc[token.address] = token;
    return acc;
  }, {});
  const infoMapKeys = Object.keys(tokenInfoMap ?? {});
  const favoriteTokens = tokenInfoMap
    ? favoriteTokenQuery.data
        ?.map((x) => ({ ...x, ...tokenInfoMap[x.address] }))
        .filter(Boolean)
        .sort(
          (a, b) =>
            new Date(b.likedAt).getTime() - new Date(a.likedAt).getTime(),
        ) ?? EMPTY_LIST
    : EMPTY_LIST;

  const filteredRows = filterRankSortTokens(search, favoriteTokens);
  const rows = useDeferredValue(filteredRows);
  const hasInfoMismatch = infoMapKeys.length < favorites.length;

  if (!hasInfoMismatch && !favoriteTokenQuery.isLoading && rows.length === 0) {
    return (
      <div className="fixed flex left-0 right-0 top-[calc(var(--header-height)+var(--h-search-bar))] bottom-[calc(var(--h-tab-bar))]">
        <EmptyState
          customIllustration={
            <div className="flex flex-col justify-center items-center">
              <Icon name="hand-swipe" className="text-lime w-8" />
              <img src="/favorite-empty-state.svg" className="mt-8" />
            </div>
          }
        >
          {search
            ? "There are no results"
            : "Swipe left on tokens to like them, or add them from their page."}
        </EmptyState>
      </div>
    );
  }

  return (
    <TokenList
      ref={scrollRef}
      rows={rows}
      id="favorite-token-list"
      searchKey={search}
      isFetching={favoriteTokenQuery.isLoading || hasInfoMismatch}
      hasNextPage={false}
      onSelectToken={onSelectToken}
      extraHeight={extraHeight}
    />
  );
}
